// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

export type Pane = {
  key: string
  label: string
  defaultParam?: string
}

export type RouteTabsProps = {
  panes: Pane[]
}

export const useRouteTabs = ({ panes }: RouteTabsProps) => {
  const location = useLocation()
  const navigate = useNavigate()

  const handleTabChange = useCallback(
    (key: string) => {
      const defaultParam = panes.find((pane) => pane.key === key)?.defaultParam

      let url = key

      if (defaultParam) {
        url = url.concat(`/${defaultParam}`)
      }

      navigate(url, { replace: true, relative: 'path' })
    },
    [navigate, panes]
  )

  return useMemo(() => {
    const splitLoc = location.pathname.split('/')
    const panesKeys = panes.map((pane) => pane.key)
    const activeKey = splitLoc.reduceRight((acc, ele) => {
      if (panesKeys.includes(ele)) {
        acc = ele
      }
      return acc
    }, '')

    return {
      activeKey,
      panes: panes,
      onChange: handleTabChange,
    }
  }, [handleTabChange, location.pathname, panes])
}
